import React, { useState, useEffect, useRef } from 'react';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  Search,
  Filter,
  Download,
  Play,
  Pause,
  Volume2,
  Clock,
  User,
  Calendar,
  MoreVertical,
  Eye,
  Trash2,
  RefreshCw,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Settings
} from 'lucide-react';
import { useWebSocket, useWebSocketData } from '../hooks/useWebSocket';
import { dashboardAPI, recordingsAPI, phoneAPI, callsAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const CallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today'); // Default to today only
  const [selectedCall, setSelectedCall] = useState(null);
  const [playingCall, setPlayingCall] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [scheduleForm, setScheduleForm] = useState({
    fromNumber: '',
    toNumber: '',
    contactName: '',
    introMessage: '',
    questions: [''],
    closingMessage: '',
    callContext: '',
    scheduledFor: '',
    maxAttempts: 3,
    callWindowStart: '09:00',
    callWindowEnd: '17:00',
    retryDelayMinutes: 60
  });
  const [scheduleVoiceModel, setScheduleVoiceModel] = useState('alloy');
  const [aiModeSelection, setAiModeSelection] = useState('standard');
  const [callProvider, setCallProvider] = useState('retell'); // 'retell' (default) or 'openai_realtime'
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState(null);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // For date picker

  const scheduleFormRef = useRef(null);

  const { isConnected, lastMessage } = useWebSocket();

  useEffect(() => {
    loadCalls();
  }, [dateFilter, statusFilter, selectedDate]);

  useEffect(() => {
    // Listen for real-time call updates
    if (lastMessage && (lastMessage.type === 'new_call' || lastMessage.type === 'call_ended')) {
      loadCalls();
    }
  }, [lastMessage]);

  useEffect(() => {
    loadAvailableNumbers();
    loadScheduledCalls();
  }, []);

  const loadCalls = async () => {
    try {
      setLoading(true);
      const params = {
        period: selectedDate ? 'custom' : dateFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchTerm || undefined,
        // Send specific date when custom date is selected
        date: selectedDate || undefined
      };
      const response = await dashboardAPI.getRecentCalls(params);
      setCalls(response.data.data.calls || []);
    } catch (error) {
      console.error('Failed to load calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableNumbers = async () => {
    try {
      const response = await phoneAPI.getMyNumbers();
      const numbers = response.data?.data || [];
      setAvailableNumbers(numbers);

      if (numbers.length > 0) {
        const defaultFrom = numbers[0].phoneNumber;
        setScheduleForm(prev => ({
          ...prev,
          fromNumber: prev.fromNumber || defaultFrom
        }));
      }
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
    }
  };

  const loadScheduledCalls = async () => {
    try {
      setLoadingScheduled(true);
      const response = await callsAPI.getScheduledCalls();
      setScheduledCalls(response.data?.data?.scheduledCalls || []);
    } catch (error) {
      console.error('Failed to load scheduled calls:', error);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const cleanupStuckCalls = async () => {
    try {
      setCleaningUp(true);
      const response = await callsAPI.cleanupStuckCalls();
      const result = response.data;
      if (result.cleaned > 0) {
        toast.success(`Cleaned up ${result.cleaned} stuck scheduled call(s)`);
        await loadScheduledCalls(); // Refresh the list
      } else {
        toast('No stuck calls found to clean up');
      }
    } catch (error) {
      console.error('Failed to cleanup stuck calls:', error);
      toast.error('Failed to cleanup stuck calls');
    } finally {
      setCleaningUp(false);
    }
  };

  const resetScheduleForm = (preserveFromNumber = true) => {
    setScheduleForm(prev => ({
      fromNumber: preserveFromNumber ? prev.fromNumber : '',
      toNumber: '',
      introMessage: '',
      questions: [''],
      closingMessage: '',
      scheduledFor: ''
    }));
    setScheduleVoiceModel('alloy');
    setAiModeSelection('standard');
  };

  /**
   * Reuse call information from a completed call to pre-fill the schedule form
   */
  const reuseCallInfo = (call) => {
    try {
      if (!call) {
        console.error('Reuse Call Info: No call data provided');
        setScheduleMessage({ success: false, data: { error: 'No call data available' } });
        return;
      }

      console.log('Reuse Call Info: Processing call', {
        callId: call.id,
        direction: call.direction,
        hasConversationContext: !!call.conversationContext,
        toNumber: call.toNumber,
        fromNumber: call.fromNumber
      });

      // Extract script info from conversation context with safe access
      // Check both conversationContext and metadata for backwards compatibility
      const conversationContext = call.conversationContext || call.metadata || {};
      const script = conversationContext.script || conversationContext.callScript || {};
      const questions = Array.isArray(script.questions) ? script.questions : [];

      // Try to get greeting from multiple sources
      const greeting = script.introMessage ||
                      conversationContext.customGreeting ||
                      conversationContext.greeting ||
                      call.customGreeting ||
                      '';

      // For outbound calls, toNumber is the destination that was called
      // For inbound calls, fromNumber is the caller
      // Fallback to callerNumber for older calls that don't have fromNumber/toNumber
      let destinationNumber;
      if (call.direction === 'outbound') {
        destinationNumber = call.toNumber || call.callerNumber;
      } else {
        destinationNumber = call.fromNumber || call.callerNumber;
      }

      if (!destinationNumber) {
        console.warn('Reuse Call Info: No destination number found', { call });
        setScheduleMessage({ success: false, data: { error: 'No phone number found in call data' } });
        return;
      }

      // Pre-fill the schedule form with safe defaults
      setScheduleForm(prev => ({
        fromNumber: prev.fromNumber, // Keep current from number selection
        toNumber: destinationNumber || '',
        introMessage: greeting,
        questions: questions.length > 0 ? questions : [''],
        closingMessage: script.closingMessage || conversationContext.closingMessage || '',
        scheduledFor: '' // Leave empty for immediate call
      }));

      console.log('Reuse Call Info: Form populated', {
        toNumber: destinationNumber,
        hasIntro: !!(script.introMessage || conversationContext.customGreeting),
        questionCount: questions.length
      });

      // Set voice model if available
      if (conversationContext.voiceConfig?.voiceModel) {
        setScheduleVoiceModel(conversationContext.voiceConfig.voiceModel);
        console.log('Reuse Call Info: Voice model set', conversationContext.voiceConfig.voiceModel);
      }

      // Close the modal
      setSelectedCall(null);

      // Show success message
      setScheduleMessage({ success: true, data: { type: 'prefilled', phoneNumber: destinationNumber } });
      setTimeout(() => setScheduleMessage(null), 3000);

      // Scroll to the schedule form
      setTimeout(() => {
        if (scheduleFormRef.current) {
          scheduleFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          console.warn('Reuse Call Info: Schedule form ref not found');
        }
      }, 100);

    } catch (error) {
      console.error('Reuse Call Info: Error processing call data', error, { call });
      setScheduleMessage({ success: false, data: { error: 'Failed to load call information' } });
    }
  };

  const handleScheduleSubmit = async (event) => {
    event.preventDefault();

    if (!scheduleForm.fromNumber || !scheduleForm.toNumber) {
      setScheduleMessage({ success: false, error: 'Select both a caller number and a destination number.' });
      return;
    }

    const questions = (scheduleForm.questions || [])
      .map(question => (question || '').trim())
      .filter(question => question.length > 0);

    let scheduledIso = null;
    if (scheduleForm.scheduledFor) {
      const candidate = new Date(scheduleForm.scheduledFor);
      if (Number.isNaN(candidate.getTime())) {
        setScheduleMessage({ success: false, error: 'Please choose a valid schedule date/time.' });
        return;
      }
      scheduledIso = candidate.toISOString();
    }

    const payload = {
      fromNumber: scheduleForm.fromNumber,
      toNumber: scheduleForm.toNumber,
      contactName: scheduleForm.contactName?.trim() || '',
      introMessage: scheduleForm.introMessage?.trim() || '',
      questions,
      closingMessage: scheduleForm.closingMessage?.trim() || '',
      callContext: scheduleForm.callContext?.trim() || '',
      scheduledFor: scheduledIso,
      voiceModel: scheduleVoiceModel,
      // Call provider: 'retell' (Retell AI) or 'vonage' (OpenAI Realtime)
      provider: callProvider === 'retell' ? 'retell' : 'vonage',
      // Retry settings
      maxAttempts: parseInt(scheduleForm.maxAttempts) || 3,
      callWindowStart: scheduleForm.callWindowStart || '09:00',
      callWindowEnd: scheduleForm.callWindowEnd || '17:00',
      retryDelayMinutes: parseInt(scheduleForm.retryDelayMinutes) || 60,
      // AI mode: 'standard' uses state machine, 'objectives' uses conversational mode
      // Pass in metadata object to match backend expectations
      metadata: {
        source: 'dashboard_schedule',
        aiMode: aiModeSelection === 'objectives' ? 'conversational_objectives' : null
      }
    };

    setScheduleSubmitting(true);
    setScheduleMessage(null);

    try {
      const response = await callsAPI.scheduleOutboundCall(payload);
      setScheduleMessage({ success: true, data: response.data.data });
      await loadScheduledCalls();
      if (!payload.scheduledFor) {
        await loadCalls();
      }
      resetScheduleForm();
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to schedule call';
      setScheduleMessage({ success: false, error: message });
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      loadCalls();
    }, 300);
  };

  const formatDuration = (seconds) => {
    if (!seconds) {return '0:00';}
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (number) => {
    if (!number) {return 'Unknown';}
    const cleaned = number.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return number;
  };

  // Format date for display
  const formatDateLabel = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <PhoneCall className="w-4 h-4 text-green-500" />;
      case 'completed':
        return <Phone className="w-4 h-4 text-blue-500" />;
      case 'failed':
        return <PhoneOff className="w-4 h-4 text-red-500" />;
      default:
        return <Phone className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get call source from metadata for display
  const getCallSource = (call) => {
    const source = call.conversationContext?.metadata?.source;
    const direction = call.direction;

    // Map source values to display labels and colors
    const sourceConfig = {
      'technical_expert_consultation': { label: 'Technical Expert', bg: 'bg-purple-100', text: 'text-purple-700', icon: '🎓' },
      'sales_campaign': { label: 'Sales Campaign', bg: 'bg-orange-100', text: 'text-orange-700', icon: '📈' },
      'sales_pipeline': { label: 'Sales Pipeline', bg: 'bg-amber-100', text: 'text-amber-700', icon: '💼' },
      'sales_pipeline_callback': { label: 'Sales Callback', bg: 'bg-amber-100', text: 'text-amber-700', icon: '📞' },
      'employee_outbound': { label: 'Employee', bg: 'bg-cyan-100', text: 'text-cyan-700', icon: '👤' },
      'demo_area': { label: 'Demo', bg: 'bg-pink-100', text: 'text-pink-700', icon: '🎯' },
      'dashboard_schedule': { label: 'Dashboard', bg: 'bg-indigo-100', text: 'text-indigo-700', icon: '📊' },
      'focus_group': { label: 'Focus Group', bg: 'bg-teal-100', text: 'text-teal-700', icon: '👥' }
    };

    // Check for specific source
    if (source && sourceConfig[source]) {
      const config = sourceConfig[source];
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
          <span>{config.icon}</span>
          {config.label}
        </span>
      );
    }

    // Fallback based on direction
    if (direction === 'outbound') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <span>📤</span>
          Outbound
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <span>📥</span>
        Inbound
      </span>
    );
  };

  // Get transfer indicator if call was transferred
  const getTransferBadge = (call) => {
    if (call.transferredTo) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700" title={`Transferred to ${call.transferredTo}`}>
          <span>🔀</span>
          Transferred
        </span>
      );
    }
    return null;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Completed' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
      queued: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Queued' },
      'no-answer': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'No Answer' },
      busy: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Busy' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.completed;
    return (
      <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.bg, config.text)}>
        {config.label}
      </span>
    );
  };

  // Get answered status badge for outbound calls
  const getAnsweredBadge = (call) => {
    if (call.direction !== 'outbound') return null;

    const wasAnswered = call.status === 'completed' && call.durationSeconds > 0;
    const noAnswer = call.status === 'no-answer' || call.status === 'busy' ||
                     (call.status === 'completed' && (!call.durationSeconds || call.durationSeconds === 0));

    if (wasAnswered) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Answered
        </span>
      );
    } else if (noAnswer || call.status === 'failed') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <PhoneMissed className="w-3 h-3 mr-1" />
          Not Answered
        </span>
      );
    }
    return null;
  };

  const handlePlayPause = async (call) => {
    if (playingCall === call.id) {
      // Pause current audio
      if (audioPlayer) {
        audioPlayer.pause();
      }
      setPlayingCall(null);
    } else {
      // Stop any currently playing audio
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
      }

      try {
        // Fetch audio with authentication
        if (call.sessionId) {
          // Show loading state
          setPlayingCall(call.id);

          // Fetch the audio blob with authentication in WAV format
          const audioBlob = await recordingsAPI.getAudio(call.sessionId, 'wav');

          // Create blob URL
          const audioBlobUrl = URL.createObjectURL(audioBlob);

          // Create audio element
          const audio = new Audio(audioBlobUrl);

          audio.addEventListener('ended', () => {
            setPlayingCall(null);
            URL.revokeObjectURL(audioBlobUrl);
          });

          audio.addEventListener('error', (e) => {
            console.error('Error playing audio:', e);
            toast.error('Failed to play audio recording. The file may not be available.');
            setPlayingCall(null);
            URL.revokeObjectURL(audioBlobUrl);
          });

          await audio.play();
          setAudioPlayer(audio);
        } else {
          toast.error('Recording not available for this call');
          setPlayingCall(null);
        }
      } catch (error) {
        console.error('Error loading audio:', error);
        toast.error('Failed to load audio recording. The file may not be available.');
        setPlayingCall(null);
      }
    }
  };

  const handleDownload = async (call) => {
    try {
      if (!call.sessionId) {
        toast.error('Recording not available for this call');
        return;
      }

      // Fetch the audio blob with authentication in WAV format
      const audioBlob = await recordingsAPI.getAudio(call.sessionId, 'wav');

      // Create blob URL and trigger download
      const audioBlobUrl = URL.createObjectURL(audioBlob);
      const link = document.createElement('a');
      link.href = audioBlobUrl;
      link.download = `call_${call.sessionId}_${new Date(call.startedAt).toISOString()}.wav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      setTimeout(() => URL.revokeObjectURL(audioBlobUrl), 100);
    } catch (error) {
      console.error('Error downloading audio:', error);
      toast.error('Failed to download audio recording. The file may not be available.');
    }
  };

  // Cleanup audio player on unmount
  useEffect(() => {
    return () => {
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
      }
    };
  }, [audioPlayer]);

  const filteredCalls = calls.filter(call => {
    const matchesSearch = !searchTerm ||
      call.fromNumber?.includes(searchTerm) ||
      call.toNumber?.includes(searchTerm) ||
      call.transcript?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Call Management</h1>
          <p className="text-gray-600 mt-1">
            Monitor and manage all voice calls in real-time
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Real-time indicator */}
          <div className="flex items-center space-x-2">
            <div className={clsx(
              'w-2 h-2 rounded-full',
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            )} />
            <span className="text-sm text-gray-600">
              {isConnected ? 'Live Updates' : 'Disconnected'}
            </span>
          </div>

          <button
            onClick={loadCalls}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>

        </div>
      </div>

      {/* Outbound Scheduling */}
      <div ref={scheduleFormRef} className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Schedule Outbound Call</h2>
            <p className="text-sm text-gray-500">Queue a scripted call with custom questions or trigger it immediately.</p>
          </div>
        </div>
        <form onSubmit={handleScheduleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Caller Number</label>
                <select
                  value={scheduleForm.fromNumber}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, fromNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  required
                >
                  <option value="">Select caller number</option>
                  {availableNumbers.map((num) => {
                    const value = num.phoneNumber;
                    const label = num.friendlyName || formatPhoneNumber(value);
                    return (
                      <option key={num.id || value} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Destination Number</label>
                  <input
                    type="tel"
                    value={scheduleForm.toNumber}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, toNumber: e.target.value })}
                    placeholder="e.g. +12183840817"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Contact Name (optional)</label>
                  <input
                    type="text"
                    value={scheduleForm.contactName}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, contactName: e.target.value })}
                    placeholder="e.g. John Smith"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Schedule For (optional)</label>
                  <input
                    type="datetime-local"
                    value={scheduleForm.scheduledFor}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduledFor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Voice Model</label>
                  <select
                    value={scheduleVoiceModel}
                    onChange={(e) => setScheduleVoiceModel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="alloy">Alloy (Neutral)</option>
                    <option value="echo">Echo (Male)</option>
                    <option value="fable">Fable (British)</option>
                    <option value="onyx">Onyx (Deep)</option>
                    <option value="nova">Nova (Female)</option>
                    <option value="shimmer">Shimmer (Soft)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Call Provider</label>
                  <select
                    value={callProvider}
                    onChange={(e) => setCallProvider(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="retell">Retell AI (Recommended)</option>
                    <option value="openai_realtime">OpenAI Realtime (Legacy)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {callProvider === 'retell'
                      ? 'Uses Retell AI for natural conversation with built-in call handling'
                      : 'Uses OpenAI Realtime API with custom voice handling'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">AI Mode</label>
                <select
                  value={aiModeSelection}
                  onChange={(e) => setAiModeSelection(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="standard">Standard (State Machine)</option>
                  <option value="objectives">AI-driven with Objectives</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {aiModeSelection === 'objectives'
                    ? 'Conversational AI uses your script as objectives rather than rigid steps.'
                    : 'Step-by-step state machine ensures script is followed exactly.'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Intro Message</label>
                <textarea
                  value={scheduleForm.introMessage}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, introMessage: e.target.value })}
                  placeholder="Hello, this is Alfred Voice following up on your appointment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-20"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-600">Questions</label>
                  <button
                    type="button"
                    onClick={() => setScheduleForm(prev => ({
                      ...prev,
                      questions: [...(prev.questions || ['']), '']
                    }))}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <span className="text-lg leading-none">+</span> Add Question
                  </button>
                </div>
                {(scheduleForm.questions || ['']).map((q, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{idx + 1}.</span>
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const updated = [...(scheduleForm.questions || [''])];
                        updated[idx] = e.target.value;
                        setScheduleForm({ ...scheduleForm, questions: updated });
                      }}
                      placeholder={idx === 0 ? "Can you confirm your availability for tomorrow at 10 AM?" : "Add another question..."}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    {(scheduleForm.questions || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updated = (scheduleForm.questions || []).filter((_, i) => i !== idx);
                          setScheduleForm({ ...scheduleForm, questions: updated.length > 0 ? updated : [''] });
                        }}
                        className="text-gray-400 hover:text-red-500 text-sm flex-shrink-0"
                        title="Remove question"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-1">One question per field. The AI will ask each question individually and wait for a response.</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Closing Message</label>
                <input
                  type="text"
                  value={scheduleForm.closingMessage}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, closingMessage: e.target.value })}
                  placeholder="Thanks for your time — we look forward to speaking with you!"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Call Context / Notes (optional)
                </label>
                <textarea
                  value={scheduleForm.callContext}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, callContext: e.target.value })}
                  placeholder="Add context for the AI about this call. For sales calls: include lead info, product interest, previous interactions, objections to address, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-24"
                />
                <p className="text-xs text-gray-400 mt-1">This context will be provided to the AI to personalize the conversation.</p>
              </div>

              {/* Retry & Call Window Settings */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Settings className="w-4 h-4 text-blue-600 mr-2" />
                  <h4 className="text-sm font-semibold text-blue-900">Retry Settings (if not answered)</h4>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Max Attempts</label>
                    <select
                      value={scheduleForm.maxAttempts}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, maxAttempts: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white"
                    >
                      <option value="1">1 attempt (no retry)</option>
                      <option value="2">2 attempts</option>
                      <option value="3">3 attempts</option>
                      <option value="4">4 attempts</option>
                      <option value="5">5 attempts</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Retry Delay</label>
                    <select
                      value={scheduleForm.retryDelayMinutes}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, retryDelayMinutes: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white"
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                      <option value="240">4 hours</option>
                      <option value="480">8 hours</option>
                      <option value="1440">24 hours</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Call Window Start</label>
                    <input
                      type="time"
                      value={scheduleForm.callWindowStart}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, callWindowStart: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">Call Window End</label>
                    <input
                      type="time"
                      value={scheduleForm.callWindowEnd}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, callWindowEnd: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Retries will only occur within this time window (your timezone).
                </p>
              </div>

              <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3">
                <p className="font-semibold text-gray-600 mb-1">Tip</p>
                <p>Leave the schedule field empty to place the call immediately. Otherwise, choose a future time to queue it.</p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            {scheduleMessage && (
              <div className={`text-sm ${scheduleMessage.success ? 'text-green-600' : 'text-red-600'}`}>
                {scheduleMessage.success
                  ? (scheduleMessage.data?.type === 'scheduled'
                    ? 'Call scheduled successfully.'
                    : scheduleMessage.data?.type === 'prefilled'
                      ? 'Call info loaded. Review and submit to place the call.'
                      : 'Call initiated successfully.')
                  : scheduleMessage.error}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => resetScheduleForm()}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                disabled={scheduleSubmitting}
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={scheduleSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {scheduleSubmitting ? 'Saving...' : 'Schedule Call'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Show scheduled calls section always, with message when empty */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Upcoming Scheduled Calls</h3>
          <div className="flex items-center space-x-2">
            {(loadingScheduled || cleaningUp) && (
              <span className="text-xs text-gray-500">
                {cleaningUp ? 'Cleaning up...' : 'Refreshing...'}
              </span>
            )}
            <button
              onClick={cleanupStuckCalls}
              disabled={cleaningUp}
              className="px-2 py-1 text-xs text-orange-600 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
              title="Clean up stuck scheduled calls (in-progress for more than 1 hour)"
            >
              <Trash2 className="w-3 h-3 inline mr-1" />
              Cleanup
            </button>
            <button
              onClick={loadScheduledCalls}
              disabled={loadingScheduled}
              className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
              title="Refresh scheduled calls"
            >
              <RefreshCw className="w-3 h-3 text-gray-600" />
            </button>
          </div>
        </div>
        {scheduledCalls.length > 0 ? (
          <div className="p-6 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-2">From</th>
                  <th className="px-4 py-2">To</th>
                  <th className="px-4 py-2">Scheduled</th>
                  <th className="px-4 py-2">Provider</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Script</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scheduledCalls.map((call) => (
                  <tr key={call.id}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{formatPhoneNumber(call.fromNumber)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{formatPhoneNumber(call.toNumber)}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{call.scheduledFor ? new Date(call.scheduledFor).toLocaleString() : 'Immediate'}</td>
                    <td className="px-4 py-2 text-xs">
                      <span className={clsx('px-2 py-0.5 rounded-full font-medium',
                        call.metadata?.provider === 'retell' ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-600')}>
                        {call.metadata?.provider === 'retell' ? 'Retell AI' : call.metadata?.provider || 'Legacy'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <span className={clsx('px-2 py-0.5 rounded-full font-medium',
                        call.status === 'pending' ? 'bg-yellow-100 text-yellow-800'
                          : call.status === 'in-progress' ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600')}>
                        {call.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {call.metadata?.script?.introMessage || call.description || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600 font-medium">No scheduled calls</p>
            <p className="text-xs text-gray-500 mt-1">
              Calls scheduled for the future will appear here. Immediate calls are sent right away and appear in the call history below.
            </p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search calls..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="queued">Queued</option>
          </select>

          {/* Date Filter Buttons + Date Picker */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setDateFilter('today'); setSelectedDate(null); }}
              className={`px-3 py-2 rounded-lg transition-colors ${
                dateFilter === 'today' && !selectedDate
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => { setDateFilter('yesterday'); setSelectedDate(null); }}
              className={`px-3 py-2 rounded-lg transition-colors ${
                dateFilter === 'yesterday' && !selectedDate
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Yesterday
            </button>
            <button
              onClick={() => { setDateFilter('7d'); setSelectedDate(null); }}
              className={`px-3 py-2 rounded-lg transition-colors ${
                dateFilter === '7d' && !selectedDate
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 hover:bg-gray-50'
              }`}
            >
              7 Days
            </button>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="date"
                value={selectedDate || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setSelectedDate(newDate);
                  if (newDate) {
                    setDateFilter('custom');
                    // Auto-expand the selected date section
                    setExpandedDates(prev => ({ ...prev, [newDate]: true }));
                  }
                }}
                className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  selectedDate ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
              />
            </div>
          </div>

          {/* Advanced Filters */}
          <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Filter className="w-4 h-4 mr-2" />
            More Filters
          </button>
        </div>
      </div>

      {/* Call Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <Phone className="w-5 h-5 text-blue-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Total Calls</p>
              <p className="text-xl font-bold text-gray-900">{calls.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <PhoneCall className="w-5 h-5 text-green-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-xl font-bold text-gray-900">
                {calls.filter(call => call.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-purple-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Avg Duration</p>
              <p className="text-xl font-bold text-gray-900">
                {formatDuration(calls.length > 0 ? Math.round(calls.reduce((acc, call) => acc + (call.durationSeconds || call.duration || 0), 0) / calls.length) : 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <PhoneOff className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Failed</p>
              <p className="text-xl font-bold text-gray-900">
                {calls.filter(call => call.status === 'failed').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Call History - Single Date View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header with date info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                {dateFilter === 'today' && !selectedDate && 'Today\'s Calls'}
                {dateFilter === 'yesterday' && !selectedDate && 'Yesterday\'s Calls'}
                {dateFilter === '7d' && !selectedDate && 'Last 7 Days'}
                {selectedDate && formatDateLabel(selectedDate)}
              </h3>
              <span className="text-sm text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                {filteredCalls.length} {filteredCalls.length === 1 ? 'call' : 'calls'}
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" text="Loading calls..." />
          </div>
        ) : filteredCalls.length === 0 ? (
          <div className="p-12 text-center">
            <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No calls found</h3>
            <p className="text-gray-500">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : selectedDate
                  ? `No calls on ${formatDateLabel(selectedDate)}. Select a different date.`
                  : 'Calls will appear here when they are made'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact / Call Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Info</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCalls.map((call) => (
                  <tr key={call.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        {getStatusIcon(call.status)}
                        <div className="ml-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {call.contact?.name || formatPhoneNumber(call.direction === 'outbound' ? call.toNumber : call.callerNumber)}
                            </span>
                            {call.direction === 'outbound' ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">→ Out</span>
                            ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">← In</span>
                            )}
                          </div>
                          {call.contact?.company && <div className="text-xs text-gray-500">{call.contact.company}</div>}
                          <div className="text-xs text-gray-400">{formatPhoneNumber(call.direction === 'outbound' ? call.toNumber : call.callerNumber)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getCallSource(call)}
                        {getTransferBadge(call)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
                        call.metadata?.provider === 'retell' ? 'bg-purple-100 text-purple-800'
                          : call.aiProvider === 'retell' ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-600')}>
                        {call.metadata?.provider === 'retell' || call.aiProvider === 'retell' ? 'Retell AI' : call.aiProvider || 'Legacy'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(call.status)}
                        {getAnsweredBadge(call)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs space-y-1">
                        {call.contact?.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {call.contact.tags.slice(0, 3).map((tag, i) => (
                              <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">{tag}</span>
                            ))}
                          </div>
                        )}
                        {call.contact?.callCount > 1 && <div className="text-gray-500">{call.contact.callCount} previous calls</div>}
                        {call.contact?.notes && <div className="text-gray-400 truncate max-w-[150px]" title={call.contact.notes}>📝 {call.contact.notes.substring(0, 30)}...</div>}
                        {call.intentClassification && <div className="text-gray-500 italic">{call.intentClassification}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDuration(call.durationSeconds)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{new Date(call.startedAt).toLocaleTimeString()}</div>
                      {(dateFilter === '7d' || selectedDate) && (
                        <div className="text-xs text-gray-400">{new Date(call.startedAt).toLocaleDateString()}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {call.recording?.available && (
                          <>
                            <button onClick={() => handleDownload(call)} className="text-blue-600 hover:text-blue-700 transition-colors" title="Download recording"><Download className="w-4 h-4" /></button>
                            <button onClick={() => handlePlayPause(call)} className="text-blue-600 hover:text-blue-700 transition-colors" title={playingCall === call.id ? 'Pause' : 'Play recording'}>
                              {playingCall === call.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </button>
                          </>
                        )}
                        <button onClick={() => setSelectedCall(call)} className="text-gray-600 hover:text-gray-700 transition-colors" title="View details"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => reuseCallInfo(call)} className="text-green-600 hover:text-green-700 transition-colors" title="Reuse call info"><RotateCcw className="w-4 h-4" /></button>
                        <button className="text-gray-600 hover:text-gray-700 transition-colors"><MoreVertical className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Call Details Modal */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Call Details</h3>
                <button
                  onClick={() => setSelectedCall(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Contact Information Section */}
                {selectedCall.contact && (
                  <div className={`rounded-lg p-4 ${selectedCall.direction === 'outbound' ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
                    <h4 className={`text-sm font-semibold mb-3 ${selectedCall.direction === 'outbound' ? 'text-blue-800' : 'text-green-800'}`}>
                      {selectedCall.direction === 'outbound' ? '📤 CONTACT INFORMATION' : '📥 CALLER INFORMATION'}
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <span className="ml-2 font-medium text-gray-900">{selectedCall.contact.name}</span>
                      </div>
                      {selectedCall.contact.company && (
                        <div>
                          <span className="text-gray-500">Company:</span>
                          <span className="ml-2 font-medium text-gray-900">{selectedCall.contact.company}</span>
                        </div>
                      )}
                      {selectedCall.contact.email && (
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <span className="ml-2 font-medium text-gray-900">{selectedCall.contact.email}</span>
                        </div>
                      )}
                      {selectedCall.contact.callCount > 0 && (
                        <div>
                          <span className="text-gray-500">Previous Calls:</span>
                          <span className="ml-2 font-medium text-gray-900">{selectedCall.contact.callCount}</span>
                        </div>
                      )}
                    </div>
                    {selectedCall.contact.tags?.length > 0 && (
                      <div className="mt-3">
                        <span className="text-gray-500 text-sm">Tags:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedCall.contact.tags.map((tag, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedCall.contact.notes && (
                      <div className="mt-3">
                        <span className="text-gray-500 text-sm">Notes:</span>
                        <p className="mt-1 text-sm text-gray-700 bg-white p-2 rounded border">{selectedCall.contact.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Call Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">From</label>
                    <p className="mt-1 text-sm text-gray-900">{formatPhoneNumber(selectedCall.callerNumber)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">To</label>
                    <p className="mt-1 text-sm text-gray-900">{formatPhoneNumber(selectedCall.toNumber)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Direction</label>
                    <div className="mt-1">
                      {selectedCall.direction === 'outbound' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          → Outbound
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          ← Inbound
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1 flex gap-2">
                      {getStatusBadge(selectedCall.status)}
                      {getAnsweredBadge(selectedCall)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duration</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDuration(selectedCall.durationSeconds)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Started</label>
                    <p className="mt-1 text-sm text-gray-900">{new Date(selectedCall.startedAt).toLocaleString()}</p>
                  </div>
                  {selectedCall.endedAt && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ended</label>
                      <p className="mt-1 text-sm text-gray-900">{new Date(selectedCall.endedAt).toLocaleString()}</p>
                    </div>
                  )}
                  {selectedCall.intentClassification && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Intent</label>
                      <p className="mt-1 text-sm text-gray-900 italic">{selectedCall.intentClassification}</p>
                    </div>
                  )}
                </div>

                {selectedCall.transcript && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Transcript</label>

                    {/* Check if dual transcripts are available (comparison mode) */}
                    {selectedCall.transcripts?.assemblyaiText && selectedCall.transcripts?.whisperText ? (
                      <div className="space-y-4">
                        {/* Comparison Metadata */}
                        {selectedCall.transcriptComparison && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center justify-between text-xs text-blue-800">
                              <span className="font-medium">STT Comparison Mode</span>
                              <div className="flex items-center space-x-4">
                                <span>Similarity: {(selectedCall.transcriptComparison.similarity * 100).toFixed(1)}%</span>
                                <span>Primary: {selectedCall.transcriptComparison.primaryProvider || 'AssemblyAI'}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Dual Transcript Display */}
                        <div className="grid grid-cols-2 gap-4">
                          {/* AssemblyAI Transcript */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-3 py-2">
                              <h4 className="text-sm font-semibold text-white flex items-center">
                                <span className="mr-2">🔊</span>
                                AssemblyAI
                              </h4>
                            </div>
                            <div className="bg-purple-50 p-4">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                {selectedCall.transcripts.assemblyaiText || selectedCall.transcript}
                              </p>
                            </div>
                          </div>

                          {/* Whisper Transcript */}
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-green-500 to-green-600 px-3 py-2">
                              <h4 className="text-sm font-semibold text-white flex items-center">
                                <span className="mr-2">🎤</span>
                                OpenAI Whisper
                              </h4>
                            </div>
                            <div className="bg-green-50 p-4">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                {selectedCall.transcripts.whisperText}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Single Transcript Display (fallback) */
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedCall.transcript}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedCall.conversationContext?.script && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Call Script</label>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-800 space-y-2">
                      {selectedCall.conversationContext.script.introMessage && (
                        <p>
                          <span className="font-semibold">Intro:</span> {selectedCall.conversationContext.script.introMessage}
                        </p>
                      )}
                      {selectedCall.conversationContext.script.questions && selectedCall.conversationContext.script.questions.length > 0 && (
                        <div>
                          <p className="font-semibold mb-1">Questions:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {selectedCall.conversationContext.script.questions.map((question, index) => (
                              <li key={index}>{question}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedCall.conversationContext.script.closingMessage && (
                        <p>
                          <span className="font-semibold">Closing:</span> {selectedCall.conversationContext.script.closingMessage}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {selectedCall.recording?.available && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Recording</label>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handlePlayPause(selectedCall)}
                        className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        {playingCall === selectedCall.id ? (
                          <>
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Play
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleDownload(selectedCall)}
                        className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </button>
                    </div>
                  </div>
                )}

                {/* Reuse Call Info Button - Always show for completed calls */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => reuseCallInfo(selectedCall)}
                    className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reuse Call Info
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Pre-fill the schedule form with this call's phone number and script
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsPage;
